// Client-side approximation of Terminus::Links::LongUrlBuilder for the live
// preview (#2627). "Close enough" by design: Addressable normalization and
// CharacterPreserver are not reproduced. Parameter values are shown unencoded
// for readability; only the keys are encoded.

// JS port of Terminus::TagFormatter.
export function formatTag(
  value,
  { forceLowercase = false, spaceReplace = null, preservedCharacters = [] } = {},
) {
  let tag = String(value ?? '').trim();
  if (forceLowercase) tag = tag.toLowerCase();
  (preservedCharacters || []).forEach((character) => {
    tag = tag.split(character.toLowerCase()).join(character);
  });
  if (spaceReplace) tag = tag.split(' ').join(spaceReplace);
  return tag;
}

// Encodes a query component (the preview only uses it for keys), matching
// LongUrlBuilder with the default project setting (Addressable, spaces as %20).
// Characters Addressable's normalize turns back into literals in a query
// component: / ! $ * ' ( ) , : @ ? = ~
// encodeURIComponent already leaves ! ~ * ' ( ) alone; undo its escapes for the rest.
export function encodeQueryComponent(text) {
  return encodeURIComponent(text).replace(/%(?:2F|24|2C|3A|40|3F|3D)/g, decodeURIComponent);
}

function orderEntries(entries, orderCustomByValue) {
  if (!orderCustomByValue) return entries;

  const custom = entries.filter((entry) => entry.param.kind === 'custom');
  // Byte-order comparison, like Ruby's sort_by on strings (not localeCompare).
  const byValue = (a, b) => {
    if (a.value < b.value) return -1;
    if (a.value > b.value) return 1;
    return 0;
  };
  const filled = custom.filter((entry) => entry.value).sort(byValue);
  const blank = custom.filter((entry) => !entry.value);
  return [...filled, ...blank, ...entries.filter((entry) => entry.param.kind !== 'custom')];
}

export function buildTaggedUrl(
  url,
  params,
  values,
  { tagFormat, orderCustomByValue } = {},
) {
  const hashIndex = url.indexOf('#');
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? null : url.slice(hashIndex);

  const entries = params.map((param) => ({
    param,
    value: formatTag(values[param.key], tagFormat),
  }));
  const segments = [{ type: 'base', text: base }];
  let separator = base.includes('?') ? '&' : '?';

  orderEntries(entries, orderCustomByValue).forEach(({ param, value }) => {
    if (!value && !param.required && !param.generated) return;

    const key = encodeQueryComponent(param.key);
    segments.push(value
      ? {
        type: 'param', separator, key, value,
      }
      : {
        type: 'placeholder', separator, key, label: param.label, reason: param.generated ? 'generated' : 'required',
      });
    separator = '&';
  });

  if (fragment) segments.push({ type: 'fragment', text: fragment });
  return segments;
}

export function segmentsToString(segments) {
  return segments.map((segment) => {
    switch (segment.type) {
      case 'param': return `${segment.separator}${segment.key}=${segment.value}`;
      case 'placeholder': return `${segment.separator}${segment.key}={${segment.label}}`;
      default: return segment.text;
    }
  }).join('');
}
